/**
 * Cross-tab idle tracking for the auto-logout timer.
 *
 * The timer has to measure how long the USER has been idle, not how long this
 * particular tab has been idle. Every tab on the origin shares one localStorage,
 * and setLoggedOut() removes auth.jwt/auth.user/auth.exp from it - so a tab left
 * open overnight reaches logoutTimePeriod entirely on its own and then deletes
 * the token out from under a tab the user is actively working in. The idle check
 * re-runs every 30s and the forgotten tab never becomes un-idle, so it keeps
 * wiping the session for as long as it stays open.
 *
 * That is TKT-000727 (and TKT-000690 / TKT-000722 before it): a user with
 * yesterday's tab still open could not stay logged in for more than a few
 * seconds at a time.
 *
 * Publishing activity through localStorage makes every tab agree on when the
 * user was last doing something, so a background tab cannot time out while
 * another tab is in use. A genuinely idle session - nobody touching any tab -
 * still logs out exactly as before.
 */

export const ACTIVITY_KEY = 'auth.lastActivity';

// mousemove fires continuously; writing on every event would hammer localStorage
// for no benefit. This throttle is also the staleness another tab can observe,
// so it has to stay well below logoutTimePeriod - otherwise a background tab can
// still time out while someone is working in another one. 1s against a 43200s
// setting leaves four orders of magnitude of headroom, and is still at most one
// write per second of mouse movement.
export const WRITE_THROTTLE_MS = 1000;

/**
 * @param storage  omit to use window.localStorage; pass null to force the
 *                 no-storage path (used by tests), or a stub to substitute one.
 * @param clock    omit to use Date.now.
 */
export const createActivityTracker = (storage, clock) => {
  const now = clock || Date.now;

  let warned = false;
  const warnDegraded = (operation, err) => {
    // Once per tracker, never per event: mark() runs on every mousemove.
    if (warned) return;
    warned = true;
    // Losing localStorage silently would put us straight back on TKT-000727 -
    // idle tracking falls back to this tab alone - so it must not pass unnoticed.
    console.warn(
      `[idle-activity] localStorage failed during ${operation}. Cross-tab idle `
      + 'tracking is degraded to this tab only, so a tab left open can sign the '
      + 'user out of the tab they are working in (TKT-000727).',
      err,
    );
  };

  const resolveStore = () => {
    if (storage !== undefined) return storage;
    if (typeof window === 'undefined') return null;
    try {
      // Reading the property itself throws when site data is blocked, so this
      // cannot live outside a try.
      return window.localStorage;
    } catch (e) {
      warnDegraded('access', e);
      return null;
    }
  };

  const store = resolveStore();

  let localActivity = now();
  // -Infinity so the first mark always publishes; a plain 0 makes the throttle
  // swallow it whenever now() is small (fake clocks in tests, and it is simply wrong).
  let lastWrite = Number.NEGATIVE_INFINITY;

  const readShared = () => {
    if (!store) return 0;
    try {
      const raw = Number(store.getItem(ACTIVITY_KEY));
      return Number.isFinite(raw) ? raw : 0;
    } catch (e) {
      warnDegraded('read', e);
      return 0;
    }
  };

  const mark = () => {
    localActivity = now();
    if (localActivity - lastWrite < WRITE_THROTTLE_MS) return;
    lastWrite = localActivity;
    if (!store) return;
    try {
      store.setItem(ACTIVITY_KEY, String(localActivity));
    } catch (e) {
      warnDegraded('write', e);
    }
  };

  const lastActivity = () => {
    const shared = readShared();
    // A shared stamp in the future cannot be genuine: either the system clock
    // moved backwards between the write and this read, or the value is corrupt.
    // It is ignored rather than clamped to now() - clamping would read as "the
    // user is active this instant" on every single check, and an unattended
    // session would stay authenticated forever. Falling back to this tab's own
    // activity is the safe direction: at worst it behaves like the old per-tab
    // code and still logs out.
    const trusted = shared > now() ? 0 : shared;
    return Math.max(localActivity, trusted);
  };

  const idleMs = () => now() - lastActivity();

  return { mark, lastActivity, idleMs };
};

export default createActivityTracker;
